import React from 'react';

import { Interpolation, TypographyProps } from '@mui/material';
import { Theme } from '@mui/material/styles';

export const typographyVariants:
  | {
      props: Partial<TypographyProps<'span', {}>>;
      style: Interpolation<{ theme: Theme }>;
    }[]
  | undefined = [
  {
    props: {
      variant: 'title',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '25px',
      lineHeight: '30.000001907348633px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'h1',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '96px',
      lineHeight: '117.02400207519531px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'h2',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '60px',
      lineHeight: '73.13999938964844px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'h3',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '48px',
      lineHeight: '58.512001037597656px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'h4',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '34px',
      lineHeight: '41.44599914550781px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'h5',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '28px',
      lineHeight: '34px',
      letterSpacing: '-0.24px',
    },
  },
  {
    props: {
      variant: 'h6',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '24.3799991607666px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'subtitle1',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '19.503999710083008px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'subtitle2',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'body1',
    },
    style: ({ theme }) => ({
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0px',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
    }),
  },
  {
    props: {
      variant: 'body2',
    },
    style: ({ theme }) => ({
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0px',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
    }),
  },
  {
    props: {
      variant: 'captionBold',
    },
    style: ({ theme }) => ({
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0px',
      [theme.breakpoints.down('md')]: {
        fontSize: '12px',
        lineHeight: '16px',
      },
    }),
  },
  {
    props: {
      variant: 'button',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '15.847000122070312px',
      letterSpacing: '0.52px',
    },
  },
  {
    props: {
      variant: 'caption',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0px',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      MsUserSelect: 'none',
      userSelect: 'none',
    },
  },
  {
    props: {
      variant: 'overline',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '10px',
      lineHeight: '12.1899995803833px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'body3',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '18px',
      lineHeight: '30.600000381469727px',
      letterSpacing: '0px',
    },
  },
  {
    props: {
      variant: 'body4',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '21px',
      lineHeight: 'normal',
      letterSpacing: '0px',
      textTransform: 'uppercase',
    },
  },
  {
    props: {
      variant: 'sarabun',
    },
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '-0.24px',
    },
  },
  {
    props: {
      variant: 'menu',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 400,
      fontSize: '10px',
      lineHeight: '16px',
      letterSpacing: 0,
    },
  },
  {
    props: {
      variant: 'inter',
    },
    style: {
      fontFamily: 'Inter',
      fontWeight: 700,
      fontSize: '100px',
      lineHeight: '121.02px',
      letterSpacing: '-0.24px',
    },
  },
];

// types
declare module '@mui/material/Typography' {
  export interface TypographyPropsVariantOverrides {
    title: true;
    body3: true;
    body4: true;
    sarabun: true;
    menu: true;
    inter: true;
    captionBold: true;
  }
}

declare module '@mui/material/styles' {
  export interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    body3?: React.CSSProperties;
    body4?: React.CSSProperties;
    sarabun?: React.CSSProperties;
    menu?: React.CSSProperties;
    inter?: React.CSSProperties;
    captionBold?: React.CSSProperties;
  }
}
